import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Plus, GraduationCap, Copy, MoreHorizontal, KeyRound, UserX, UserCheck, Pencil } from "lucide-react";

export default function Faculty() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", mobile: "" });
    const [created, setCreated] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", mobile: "" });

    const load = async () => { const { data } = await api.get("/faculty"); setItems(data.items); };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/faculty", form);
            setCreated(data);
            setForm({ name: "", email: "", mobile: "" });
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

    const toggleStatus = async (f) => {
        const action = f.status === "active" ? "deactivate" : "activate";
        await api.post(`/faculty/${f.id}/${action}`);
        toast.success(`Faculty ${action}d`);
        load();
    };

    const resetPw = async (f) => {
        const { data } = await api.post(`/faculty/${f.id}/reset-password`);
        toast.success("Password reset");
        copy(data.temp_password);
    };

    const openEdit = (f) => {
        setEditTarget(f);
        setEditForm({ name: f.name || "", mobile: f.mobile || "" });
    };

    const submitEdit = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/faculty/${editTarget.id}`, editForm);
            toast.success("Faculty updated");
            setEditTarget(null);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-6" data-testid="faculty-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Manage</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Faculty</h1>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreated(null); }}>
                    <DialogTrigger asChild><Button className="mx-btn-primary" data-testid="add-faculty-btn"><Plus className="h-4 w-4 mr-2" /> Add faculty</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>{created ? "Faculty created" : "Add faculty"}</DialogTitle></DialogHeader>
                        {created ? (
                            <div className="space-y-3">
                                <div className="bg-amber-gradient text-white rounded-xl p-4">
                                    <div className="text-xs uppercase tracking-widest font-bold text-white/90">Temporary password</div>
                                    <div className="flex items-center justify-between mt-1">
                                        <code className="font-bold text-lg">{created.temp_password}</code>
                                        <Button size="sm" variant="ghost" onClick={() => copy(created.temp_password)} className="text-white hover:bg-white/20"><Copy className="h-4 w-4 mr-1.5" /> Copy</Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-3">
                                <div><Label>Name</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="faculty-name" /></div>
                                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="faculty-email" /></div>
                                <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} /></div>
                                <DialogFooter><Button type="submit" className="mx-btn-primary" data-testid="faculty-submit">Create</Button></DialogFooter>
                            </form>
                        )}
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="mx-card">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Faculty</TableHead>
                                <TableHead>Mobile</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map(f => (
                                <TableRow key={f.id} data-testid={`faculty-row-${f.id}`}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-sm">{f.name?.[0]}</div>
                                            <div>
                                                <div className="font-semibold text-sm">{f.name}</div>
                                                <div className="text-xs text-muted-foreground">{f.email}</div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm">{f.mobile || "—"}</TableCell>
                                    <TableCell>
                                        <Badge variant={f.status === "active" ? "default" : "secondary"} className={f.status === "active" ? "bg-[#16A34A] hover:bg-[#16A34A]" : ""}>
                                            {f.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" data-testid={`faculty-row-actions-${f.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openEdit(f)} data-testid={`faculty-edit-${f.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => resetPw(f)} data-testid={`faculty-reset-password-${f.id}`}><KeyRound className="h-4 w-4 mr-2" /> Reset password</DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => toggleStatus(f)} data-testid={`faculty-toggle-status-${f.id}`}>
                                                    {f.status === "active" ? <><UserX className="h-4 w-4 mr-2" /> Deactivate</> : <><UserCheck className="h-4 w-4 mr-2" /> Activate</>}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                                        <GraduationCap className="h-10 w-10 mx-auto mb-3" />No faculty yet
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Edit faculty</DialogTitle></DialogHeader>
                    <form onSubmit={submitEdit} className="space-y-3">
                        <div><Label>Name</Label><Input required value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} data-testid="faculty-edit-name" /></div>
                        <div><Label>Mobile</Label><Input value={editForm.mobile} onChange={e => setEditForm({ ...editForm, mobile: e.target.value })} data-testid="faculty-edit-mobile" /></div>
                        <DialogFooter><Button type="submit" className="mx-btn-primary" data-testid="faculty-edit-submit">Save changes</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
